import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Checkbox } from '@shared/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'

type Role = { id: string; name: string; description?: string; user_count: number }
type Permission = { key: string; name: string; description?: string; category: string }

type Preset = { name: string; permissionKeys: string[] }

type TenantInfo = { id: string; name: string }

export default function RolesPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const [idToken, setIdToken] = useState<string>('')
  useEffect(() => { (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); setIdToken(t || '') })() }, [])
  const [tenant, setTenant] = useState<TenantInfo | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [presets, setPresets] = useState<Preset[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')
  const [rolePermissionKeys, setRolePermissionKeys] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [renamingRoleId, setRenamingRoleId] = useState<string>('')
  const [renameValue, setRenameValue] = useState('')
  const [search, setSearch] = useState('')
  const [selectedPresetName, setSelectedPresetName] = useState('')

  // Normalize categories and order
  const categoryOrder = ['service_requests', 'invoices', 'inventory', 'docs', 'attendance', 'templates', 'settings', 'time', 'users', 'roles', 'other']
  const toCategoryKey = (c: string) => (c || 'other').toLowerCase().replace(/\s+/g, '_')
  const toTitle = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
  const getPermissionGroupKey = (p: Permission) => {
    const prefix = (p.key || '').split(':')[0] || ''
    const normalizedPrefix = toCategoryKey(prefix)
    if (normalizedPrefix && normalizedPrefix !== 'other') return normalizedPrefix
    const normalizedCategory = toCategoryKey(p.category)
    return normalizedCategory || 'other'
  }

  const groupedPermissions = useMemo(() => {
    const map: Record<string, Permission[]> = {}
    const needle = search.trim().toLowerCase()
    for (const p of permissions) {
      if (needle && !p.name.toLowerCase().includes(needle) && !p.key.toLowerCase().includes(needle)) continue
      const cat = getPermissionGroupKey(p)
      if (!map[cat]) map[cat] = []
      map[cat].push(p)
    }
    // sort each group
    for (const k of Object.keys(map)) map[k].sort((a,b)=>a.name.localeCompare(b.name))
    return map
  }, [permissions, search])

  const orderedCategories = useMemo(() => {
    const keys = Object.keys(groupedPermissions)
    const known = categoryOrder.filter(k => keys.includes(k))
    const rest = keys.filter(k => !categoryOrder.includes(k)).sort()
    return [...known, ...rest]
  }, [groupedPermissions])

  const load = async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      const hdrs = idToken ? { 'Authorization': `Bearer ${idToken}` } : {}
      const [tRes, rRes, pRes, prRes] = await Promise.all([
        fetch(apiUrl(`/api/admin/tenants/${tenantId}`), { headers: hdrs }),
        fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles`), { headers: hdrs }),
        fetch(apiUrl(`/api/admin/permissions`), { headers: hdrs }),
        fetch(apiUrl(`/api/admin/permission-presets`), { headers: hdrs }),
      ])
      if (!tRes.ok || !rRes.ok || !pRes.ok || !prRes.ok) throw new Error('Failed to load data')
      const tJson = await tRes.json()
      const rJson = await rRes.json()
      const pJson = await pRes.json()
      const prJson = await prRes.json()
      setTenant({ id: tJson.id, name: tJson.name })
      setRoles(rJson.roles || [])
      setPermissions(pJson.permissions || [])
      setPresets(prJson.presets || [])
      if (!selectedRoleId && (rJson.roles?.length ?? 0) > 0) setSelectedRoleId(rJson.roles[0].id)
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load roles/permissions', variant: 'destructive' })
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [tenantId])

  const loadRolePermissions = async (roleId: string) => {
    if (!tenantId || !roleId) return
    try {
      const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles/${roleId}/permissions`), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
      if (!res.ok) throw new Error('Failed to load role permissions')
      const json = await res.json()
      setRolePermissionKeys(json.permissionKeys || [])
    } catch (e) { toast({ title: 'Error', description: 'Failed to load role permissions', variant: 'destructive' }) }
  }

  useEffect(() => { if (selectedRoleId) loadRolePermissions(selectedRoleId) }, [selectedRoleId])

  const togglePermission = (key: string, checked: boolean) => {
    setRolePermissionKeys((prev) => checked ? Array.from(new Set([...prev, key])) : prev.filter(k => k !== key))
  }

  const savePermissions = async () => {
    if (!tenantId || !selectedRoleId) return
    try {
      const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles/${selectedRoleId}/permissions`), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ permissionKeys: rolePermissionKeys }) })
      if (!res.ok) throw new Error('Failed to update role permissions')
      toast({ title: 'Permissions updated' })
    } catch (e: any) { toast({ title: 'Error', description: e.message || 'Failed to update', variant: 'destructive' }) }
  }

  const createRole = async () => {
    if (!tenantId || !newRoleName.trim()) return
    setCreating(true)
    try {
      const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ name: newRoleName.trim() }) })
      if (!res.ok) throw new Error('Failed to create role')
      setNewRoleName(''); await load(); toast({ title: 'Role created' })
    } catch (e: any) { toast({ title: 'Error', description: e.message || 'Failed to create role', variant: 'destructive' }) } finally { setCreating(false) }
  }

  const renameRole = async (roleId: string) => {
    if (!tenantId || !renameValue.trim()) { setRenamingRoleId(''); return }
    try {
      const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles/${roleId}`), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ name: renameValue.trim() }) })
      if (!res.ok) throw new Error('Failed to rename role')
      setRenamingRoleId(''); setRenameValue(''); await load(); toast({ title: 'Role updated' })
    } catch (e: any) { toast({ title: 'Error', description: e.message || 'Failed to update role', variant: 'destructive' }) }
  }

  const deleteRole = async (roleId: string) => {
    if (!tenantId) return
    if (!confirm('Delete this role?')) return
    try {
      const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles/${roleId}`), { method: 'DELETE', headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to delete role') }
      await load(); toast({ title: 'Role deleted' })
    } catch (e: any) { toast({ title: 'Error', description: e.message || 'Failed to delete role', variant: 'destructive' }) }
  }

  const applyPreset = (presetName: string) => { const preset = presets.find(p => p.name === presetName); if (!preset) return; setRolePermissionKeys(preset.permissionKeys) }
  const cloneRole = async (roleId: string) => { if (!tenantId) return; try { const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles/${roleId}/clone`), { method: 'POST', headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} }); if (!res.ok) throw new Error('Failed to clone role'); await load(); toast({ title: 'Role cloned' }) } catch (e: any) { toast({ title: 'Error', description: e.message || 'Failed to clone role', variant: 'destructive' }) } }

  if (loading) return <div className="p-6">Loading...</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Roles & Permissions</h1>
          {tenant && <div className="text-sm text-muted-foreground">Tenant: {tenant.name}</div>}
        </div>
        <div className="space-x-2">
          <Link to={`/tenants/${tenantId}`}><Button variant="outline">Back to Tenant</Button></Link>
          <Link to={`/tenants/${tenantId}/users`}><Button variant="outline">Manage Users</Button></Link>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Roles column (1/3 on desktop) */}
        <div className="md:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Roles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {roles.map((r) => (
                  <div key={r.id} className={`p-2 border border-border rounded cursor-pointer ${selectedRoleId===r.id ? 'bg-accent' : ''}`} onClick={() => setSelectedRoleId(r.id)}>
                    <div className="flex items-center justify-between">
                      {renamingRoleId === r.id ? (
                        <div className="flex items-center space-x-2 w-full">
                          <Input value={renameValue} onChange={e=>setRenameValue(e.target.value)} placeholder="Role name" />
                          <Button size="sm" onClick={()=>renameRole(r.id)}>Save</Button>
                          <Button size="sm" variant="outline" onClick={()=>{setRenamingRoleId(''); setRenameValue('')}}>Cancel</Button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground">Users: {r.user_count}</div>
                          </div>
                          <div className="space-x-2">
                            <Button size="sm" variant="outline" onClick={()=>{setRenamingRoleId(r.id); setRenameValue(r.name)}}>Rename</Button>
                            <Button size="sm" variant="outline" onClick={()=>cloneRole(r.id)}>Clone</Button>
                            <Button size="sm" variant="destructive" onClick={()=>deleteRole(r.id)}>Delete</Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                <Label>New Role</Label>
                <div className="flex items-center space-x-2">
                  <Input value={newRoleName} onChange={e=>setNewRoleName(e.target.value)} placeholder="Role name" />
                  <Button onClick={createRole} disabled={creating || !newRoleName.trim()}>Create</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Permissions column (2/3 on desktop) */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Permissions {selectedRoleId ? '' : '(select a role)'}</CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedRoleId ? (
                <div>Select a role to edit permissions</div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Input placeholder="Search permissions" value={search} onChange={(e)=>setSearch(e.target.value)} />
                    {presets.length > 0 && (
                      <Select value={selectedPresetName} onValueChange={(value)=>{ setSelectedPresetName(value); applyPreset(value); }}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Apply preset..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Apply preset...</SelectItem>
                          {presets.map((p)=> (<SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button onClick={savePermissions}>Save</Button>
                  </div>
                  {orderedCategories.map((catKey) => (
                    <div key={catKey}>
                      <div className="font-medium mb-2 text-foreground">{toTitle(catKey)}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(groupedPermissions[catKey]||[]).map(p => (
                          <label key={p.key} className="flex items-center space-x-2 text-foreground">
                            <Checkbox checked={rolePermissionKeys.includes(p.key)} onCheckedChange={(v)=>togglePermission(p.key, Boolean(v))} />
                            <span>{p.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}


